import { Loader2, Check, CloudOff, AlertCircle } from 'lucide-react'
import type { NoteSaveStatus, NoteSaveFailReason } from '@store/slices/saveIndicatorSlice'

/**
 * 保存同步指示器（唯一渲染实现，Header 右区首位显示）
 *
 * saving  → "正在保存"（实际写入 collection，乐观事务已提交）
 * saved   → "保存成功"（Electric 云端持久化确认，闪现 2s 后由上报方清除）
 * failed  → 失败态（保持显示直至下次保存开始或笔记切换，不闪没）：
 *   conflict = OCC 冲突——服务器内容已被其他设备更新，本地编辑已让位并显示最新
 *   quota    = 库配额超限——永久错误已停止重试，提示清理空间或升级会员
 *   error    = 网络/持久化失败——保持 EDITING 自动重试中，编辑仍在本地不丢
 *
 * 编辑中的防抖待存阶段不显示（避免"编辑中/正在保存"语义混淆）
 */
const SaveStatusIndicator: React.FC<{ status?: NoteSaveStatus; failReason?: NoteSaveFailReason }> = ({ status, failReason }) => {
  if (status === 'saving') {
    return (
      <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
        <Loader2 className="w-3 h-3 animate-spin" />
        正在保存
      </span>
    )
  }
  if (status === 'saved') {
    return (
      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 whitespace-nowrap">
        <Check className="w-3 h-3" />
        保存成功
      </span>
    )
  }
  if (status === 'failed') {
    if (failReason === 'conflict') {
      return (
        <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap" title="服务器内容已被其他设备更新，已为你显示最新版本">
          <CloudOff className="w-3 h-3" />
          刚刚对齐云端最新数据
        </span>
      )
    }
    if (failReason === 'quota') {
      // ★ 2026-10-29：库配额超限（永久错误，已停止重试）——明确告知原因与出路，
      //   不再显示误导性的"重试中"（重试不可能成功）
      return (
        <span className="flex items-center gap-1 text-xs text-red-500 dark:text-red-400 whitespace-nowrap" title="数据库总空间已满，编辑内容仍保留在本页不丢失。请删除笔记或清空回收站释放空间，或升级会员扩容">
          <AlertCircle className="w-3 h-3" />
          空间已满，未保存
        </span>
      )
    }
    return (
      <span className="flex items-center gap-1 text-xs text-red-500 dark:text-red-400 whitespace-nowrap" title="保存失败，网络恢复后将自动重试，编辑不会丢失">
        <AlertCircle className="w-3 h-3" />
        保存失败，重试中
      </span>
    )
  }
  return null
}

export default SaveStatusIndicator
